import { useEffect, useRef, useState } from "react";
import { Download, Printer, X, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Reusable PDF preview modal used by Invoice / Quotation / Delivery Order
 * (and any other document that has a `/pdf` endpoint).
 *
 * The component fetches the PDF as a blob when it opens, renders it inside an
 * <iframe>, and exposes Download + Print buttons in the footer. Uses an
 * ObjectURL so the browser's PDF viewer (with its own zoom/paging controls)
 * takes over, giving the user a real preview before they commit to
 * downloading or printing.
 *
 * Props:
 *   open, onOpenChange     — controlled dialog state
 *   title                  — dialog title (e.g. "Invoice INV-2026-0055")
 *   fetchBlob              — async fn returning an axios response whose
 *                            `data` is the raw PDF (usually
 *                            client.get(url, { responseType: 'blob' }))
 *   filename               — download filename, e.g. "invoice-INV-2026-0055.pdf"
 */
export function PdfPreviewDialog({ open, onOpenChange, title, fetchBlob, filename, fetchPngBlob, pngFilename }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [pngBusy, setPngBusy] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl = null;
    async function load() {
      if (!open || !fetchBlob) return;
      setBusy(true);
      setErr(null);
      try {
        const res = await fetchBlob();
        const blob = new Blob([res.data], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) setUrl(objectUrl);
      } catch (e) {
        setErr(e?.response?.data?.detail || e?.message || "Failed to load PDF");
      } finally {
        setBusy(false);
      }
    }
    load();
    return () => {
      revoked = true;
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
      setUrl(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function download() {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function printNow() {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      iframe.focus();
      iframe.contentWindow.print();
    } catch {
      toast.info("Popup blocked — use the browser's Print button in the preview");
    }
  }

  async function saveAsImage() {
    if (!fetchPngBlob) return;
    setPngBusy(true);
    try {
      const res = await fetchPngBlob();
      const blob = new Blob([res.data], { type: "image/png" });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = pngFilename || (filename || "document.pdf").replace(/\.pdf$/i, ".png");
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
      toast.success("Image ready — share it on WhatsApp");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not build image");
    } finally {
      setPngBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="pdf-preview-dialog"
        className="sm:max-w-none max-w-none w-[min(96vw,920px)] h-[96vh] flex flex-col p-0 gap-0"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0">
          <DialogTitle className="font-display flex items-center gap-2">
            {title || "Document preview"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Preview the document — download or print when you're ready.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 bg-slate-100 relative">
          {busy && (
            <div className="absolute inset-0 grid place-items-center bg-white/60">
              <Loader2 className="size-5 animate-spin text-primary" />
            </div>
          )}
          {err && !busy && (
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-center max-w-sm px-6">
                <p className="text-sm font-semibold text-red-700">Preview failed</p>
                <p className="text-xs text-muted-foreground mt-1">{err}</p>
              </div>
            </div>
          )}
          {url && (
            <iframe
              ref={iframeRef}
              data-testid="pdf-preview-frame"
              title={title || "PDF preview"}
              src={url}
              className="w-full h-full border-0"
            />
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-white shrink-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)}
                  data-testid="pdf-preview-close">
            <X className="size-4 mr-1.5" /> Close
          </Button>
          <Button
            variant="outline"
            onClick={printNow}
            disabled={!url}
            data-testid="pdf-preview-print"
          >
            <Printer className="size-4 mr-1.5" /> Print
          </Button>
          {fetchPngBlob && (
            <Button
              variant="outline"
              onClick={saveAsImage}
              disabled={!url || pngBusy}
              data-testid="pdf-preview-save-png"
              className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
              title="Save page 1 as a PNG image for WhatsApp"
            >
              {pngBusy
                ? <Loader2 className="size-4 mr-1.5 animate-spin" />
                : <ImageIcon className="size-4 mr-1.5" />}
              Save as image
            </Button>
          )}
          <Button
            onClick={download}
            disabled={!url}
            className="bg-primary hover:bg-primary/90"
            data-testid="pdf-preview-download"
          >
            <Download className="size-4 mr-1.5" /> Download
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
